import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import { useState } from "react";

import { DiffFieldOverlay } from "@/features/rich-text/diff";
import type { AtomicFieldAttrs, FieldUiOptions } from "../types";
import { EnumFieldView } from "./enum-field-view";
import { FieldHeader } from "./field-header";
import { useDocumentHasOwnerOverrides, useNodeIsActive } from "./field-view-state";
import { OwnerFieldView } from "./owner-field-view";
import { TextFieldView } from "./text-field-view";
import { useQualityGuardFieldIssues } from "./use-quality-guard-field-issues";

/**
 * Router NodeView for AtomicField nodes.
 *
 * Dispatches to the appropriate sub-view based on the `fieldType` attribute:
 * - "owner" → OwnerFieldView (combobox for user selection)
 * - "enum" → EnumFieldView (dropdown for predefined options)
 * - "text" → TextFieldView (simple text input)
 */
interface AtomicFieldViewProps extends NodeViewProps {
  fieldUiOptions?: FieldUiOptions;
}

export function AtomicFieldView(props: AtomicFieldViewProps) {
  const { node, editor, updateAttributes, getPos, fieldUiOptions } = props;
  const {
    fieldType,
    label,
    guidance,
    expectedMentionTypes,
    ownerId,
    fieldId,
    value,
    _diffStatus,
    _diffOldValue,
  } = node.attrs as AtomicFieldAttrs;
  const selectionActive = useNodeIsActive({ editor, getPos });
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const isActive = selectionActive || hasFocusWithin;
  const hasOwnerOverrides = useDocumentHasOwnerOverrides(editor);
  const isEmpty = typeof value !== "string" || value.trim().length === 0;
  const entityOwnerId = fieldUiOptions?.getEntityOwnerId?.() ?? null;
  const showGuardPlaceholder = fieldUiOptions?.getShouldShowGuardPlaceholder?.() ?? false;
  const qualityGuardIssues = useQualityGuardFieldIssues(fieldUiOptions, fieldId);
  const parsedDiffOldValue = (() => {
    if (!_diffOldValue) return null;
    try {
      const parsed = JSON.parse(_diffOldValue) as { value?: unknown };
      if (parsed.value == null) return null;
      return String(parsed.value);
    } catch {
      return null;
    }
  })();

  const control = (
    <div className="flex-1 min-w-0">
      {fieldType === "owner" && <OwnerFieldView {...props} />}
      {fieldType === "enum" && <EnumFieldView {...props} />}
      {fieldType === "text" && <TextFieldView {...props} />}
    </div>
  );

  return (
    <NodeViewWrapper
      data-type="atomic-field"
      data-field-type={fieldType}
      data-df-section-anchor=""
      data-field-id={fieldId}
      contentEditable={false}
      className="mb-4"
    >
      <div className="space-y-2.5 py-1.5">
        <FieldHeader
          fieldId={fieldId}
          label={label}
          guidance={guidance}
          expectedMentionTypes={expectedMentionTypes}
          diffStatus={_diffStatus}
          ownerId={ownerId}
          entityOwnerId={entityOwnerId}
          showGuardPlaceholder={showGuardPlaceholder}
          qualityGuardIssues={qualityGuardIssues}
          showOwner={hasOwnerOverrides}
          onOwnerChange={(nextOwnerId) => {
            // Dual write by design: updateAttributes keeps the owner in the editor doc so the next
            // versioned save persists it, while onAssignSectionOwner commits it to the entity
            // immediately and emits the assignment notification.
            updateAttributes({ ownerId: nextOwnerId });
            fieldUiOptions?.onAssignSectionOwner?.({
              fieldId,
              label,
              ownerId: nextOwnerId,
            });
          }}
          canEditOwner={editor.isEditable}
          showMeatball={editor.isEditable}
          showGuidanceCallout={Boolean(guidance && isActive && !isEmpty)}
          onAskClaudia={fieldUiOptions?.onAskClaudia}
          onOpenQualityGuard={fieldUiOptions?.onOpenQualityGuard}
          titleClassName="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
          ownerClassName="text-neutral-dim"
        />

        <div
          className="min-w-0"
          onFocusCapture={() => setHasFocusWithin(true)}
          onBlurCapture={(event) => {
            const nextTarget = event.relatedTarget;
            if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
            setHasFocusWithin(false);
          }}
        >
          {_diffStatus ? (
            <DiffFieldOverlay diffStatus={_diffStatus} oldValueLabel={parsedDiffOldValue}>
              {control}
            </DiffFieldOverlay>
          ) : (
            control
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
}
